import PropTypes from 'prop-types';
import { Form, Input, Select } from 'antd';
import { countryCallingCodeOptions } from '../countryCodes';

const DEFAULT_SELECT_WIDTH = 90;

/**
 * Shared phone number input: country code Select + number Input.
 * Used in owner, user, veterinarian, groomer and profile forms.
 * @param {string} fieldName - e.g. 'cell_phone_number'
 * @param {number} selectWidth - Width in px for country code Select
 * @param {string[]} [namePrefix] - Optional path prefix (e.g. ['secondary_profile'] for nested field)
 * @returns {JSX.Element} - The PhoneNumberInput component.
 */
export const PhoneNumberInput = ({
  fieldName,
  selectWidth = DEFAULT_SELECT_WIDTH,
  namePrefix
}) => {
  const prefix = Array.isArray(namePrefix) ? namePrefix : [];

  const countryCodeName = [...prefix, fieldName, 'country_code'];
  const numberName = [...prefix, fieldName, 'number'];

  return (
    <Input.Group compact>
      <Form.Item noStyle name={countryCodeName} initialValue="+33">
        <Select
          style={{ width: selectWidth }}
          options={countryCallingCodeOptions}
        />
      </Form.Item>
      <Form.Item noStyle name={numberName}>
        <Input type="tel" style={{ width: `calc(100% - ${selectWidth}px)` }} />
      </Form.Item>
    </Input.Group>
  );
};

PhoneNumberInput.propTypes = {
  fieldName: PropTypes.string.isRequired,
  selectWidth: PropTypes.number.isRequired,
  namePrefix: PropTypes.arrayOf(PropTypes.string)
};

PhoneNumberInput.defaultProps = {
  namePrefix: []
};

/**
 * Returns a form field config for a phone number (cell or landline).
 * @param {string} fieldName - e.g. 'cell_phone_number' or 'phone_number'
 * @param {string[]|undefined} [namePrefix] - Optional path prefix (e.g. ['secondary_profile'] for nested field)
 * @param {number} [selectWidth=90] - Width in px for the country code Select (e.g. 80 for pensioners)
 * @returns {Object} - The form field config.
 */
export const createPhoneNumberFieldConfig = (
  fieldName,
  namePrefix,
  selectWidth = DEFAULT_SELECT_WIDTH
) => {
  const prefix = Array.isArray(namePrefix) ? namePrefix : [];
  return {
    name: [...prefix, fieldName],
    label: `${fieldName}.number`,
    input: (
      <PhoneNumberInput
        fieldName={fieldName}
        selectWidth={selectWidth}
        namePrefix={prefix}
      />
    )
  };
};
